import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import * as XLSX from "xlsx";
import { api } from "../../lib/api";
/**
 * Parses the combined workbook this project already produces.
 * Structure per sheet:
 *   row 1: title (merged, ignored)
 *   row 2: base group headers + Exam N (merged over 4 cols each)
 *   row 3: Part 1 | Part 2 | Total | Grade  (per exam)
 *   row 4+: data rows
 * Columns: No · Name · Gender (optional) · then blocks of {Part1, Part2, Total, Grade}
 */
function parseWorkbook(wb) {
    const out = { classes: [], warnings: [] };
    for (const sheetName of wb.SheetNames) {
        const match = sheetName.match(/grade\s*(\d+)/i);
        if (!match) {
            out.warnings.push(`Skipped sheet "${sheetName}" (name not "Grade N")`);
            continue;
        }
        const classId = parseInt(match[1], 10);
        if (![6, 7, 8, 9, 10, 11].includes(classId)) {
            out.warnings.push(`Skipped "${sheetName}" — grade ${classId} not seeded`);
            continue;
        }
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], {
            header: 1, defval: null, raw: true,
        });
        // Find header row (row 3, 0-indexed 2) — the one with "Part 1"
        const headerRowIdx = rows.findIndex((r) => r?.some((c) => typeof c === "string" && /part\s*1/i.test(c)));
        if (headerRowIdx < 0) {
            out.warnings.push(`No header row in "${sheetName}"`);
            continue;
        }
        const headerRow = rows[headerRowIdx];
        const groupRow = rows[headerRowIdx - 1] ?? [];
        // Find "Gender" column (optional)
        const hasGender = headerRow.some((c) => typeof c === "string" && /gender/i.test(c)) ||
            groupRow.some((c) => typeof c === "string" && /gender/i.test(c));
        const baseCols = hasGender ? 3 : 2;
        // Discover exam blocks: each block is 4 consecutive columns starting at baseCols.
        // Prefer to read exam name from groupRow, fall back to "Exam N".
        const exams = [];
        for (let c = baseCols; c < headerRow.length; c += 4) {
            if (!headerRow[c] || !/part\s*1/i.test(String(headerRow[c])))
                break;
            const name = String(groupRow[c] ?? `Exam ${exams.length + 1}`).trim();
            exams.push({ name, startCol: c });
        }
        const cls = {
            class_id: classId,
            students: [],
            exams: exams.map((e) => ({ name: e.name, part1_max: 40, part2_max: 60, scores: [] })),
        };
        for (let r = headerRowIdx + 1; r < rows.length; r++) {
            const row = rows[r];
            if (!row)
                continue;
            const rawNo = row[0];
            const rawName = row[1];
            if (rawNo == null || rawName == null || String(rawName).trim() === "")
                continue;
            const rollNo = typeof rawNo === "number" ? rawNo : parseInt(String(rawNo), 10);
            if (!Number.isFinite(rollNo))
                continue;
            const fullName = String(rawName).trim();
            const gender = hasGender ? (row[2] === "Boy" || row[2] === "Girl" ? row[2] : undefined) : undefined;
            cls.students.push({ roll_no: rollNo, full_name: fullName, gender });
            exams.forEach((e, i) => {
                const p1 = row[e.startCol];
                const p2 = row[e.startCol + 1];
                const empty = (v) => v === null || v === undefined || v === "";
                if (empty(p1) && empty(p2))
                    return;
                const norm = (v) => {
                    if (empty(v))
                        return null;
                    if (typeof v === "string" && v.trim().toUpperCase() === "AB")
                        return "AB";
                    const n = typeof v === "number" ? v : parseFloat(String(v));
                    return Number.isFinite(n) ? n : null;
                };
                cls.exams[i].scores.push({ roll_no: rollNo, part1: norm(p1), part2: norm(p2) });
            });
        }
        // Drop exams that ended up empty (Grade 9 has 3 empty blocks in the template)
        cls.exams = cls.exams.filter((e) => e.scores.length > 0);
        out.classes.push(cls);
    }
    return out;
}
export default function AdminImport() {
    const [preview, setPreview] = useState(null);
    const [filename, setFilename] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const onFile = async (f) => {
        if (!f)
            return;
        setFilename(f.name);
        const buf = await f.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        setPreview(parseWorkbook(wb));
        setResult(null);
    };
    const runImport = async () => {
        if (!preview)
            return;
        setBusy(true);
        try {
            const res = await api.post("/api/admin/import", { classes: preview.classes });
            setResult(`Imported ${res.students} student(s), ${res.exams} new exam(s), ${res.scores} score(s) across ${res.classes} class(es).` +
                (res.skipped ? ` ${res.skipped} score(s) skipped (unknown roll no).` : ""));
        }
        catch (e) {
            setResult(`Failed: ${e.message}`);
        }
        finally {
            setBusy(false);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "eyebrow", children: "Admin" }), _jsx("h1", { className: "font-display text-3xl mb-5", children: "Import from xlsx" }), _jsxs("div", { className: "card p-6 mb-4", children: [_jsx("p", { className: "text-sm text-slate2 mb-3", children: "Upload the combined workbook to seed classes 6\u201311. Existing students matched by roll number are kept; new students are added. Exams are matched by name; scores are upserted." }), _jsxs("div", { className: "flex flex-wrap items-center gap-3 mb-4", children: [_jsx("a", { href: "/IELL_Karaitivu_Marksheet_Template.xlsx", download: true, className: "btn-ghost text-xs", children: "\u2193 Download template" }), _jsxs("span", { className: "text-xs text-slate2", children: ["One sheet per grade \u00B7 Part 1 / Part 2 / Total / Grade per exam \u00B7 type ", _jsx("code", { className: "bg-paper px-1 rounded", children: "AB" }), " for absent"] })] }), _jsx("input", { type: "file", accept: ".xlsx,.xls", onChange: (e) => onFile(e.target.files?.[0] ?? null), className: "text-sm block" })] }), preview && (_jsxs("div", { className: "card p-5 mb-4", children: [_jsxs("div", { className: "flex items-baseline justify-between gap-3 mb-3", children: [_jsxs("div", { children: [_jsx("div", { className: "eyebrow", children: "Preview" }), _jsx("div", { className: "font-display text-lg mt-1", children: filename })] }), _jsx("button", { className: "btn", disabled: busy, onClick: runImport, children: busy ? "Importing…" : "Import" })] }), preview.warnings.length > 0 && (_jsx("ul", { className: "text-xs text-warn mb-3", children: preview.warnings.map((w, i) => _jsxs("li", { children: ["\u2022 ", w] }, i)) })), _jsx("div", { className: "grid gap-2", children: preview.classes.map((c) => (_jsxs("div", { className: "border border-line rounded p-3 bg-paper", children: [_jsxs("div", { className: "font-semibold", children: ["Grade ", c.class_id] }), _jsxs("div", { className: "text-xs text-slate2 mt-1", children: [_jsx("span", { className: "font-mono text-ink", children: c.students.length }), " student(s) \u00B7", " ", _jsx("span", { className: "font-mono text-ink", children: c.exams.length }), " exam(s) \u00B7", " ", _jsx("span", { className: "font-mono text-ink", children: c.exams.reduce((n, e) => n + e.scores.length, 0) }), " score(s)"] })] }, c.class_id))) })] })), result && (_jsx("div", { className: "card p-4 text-sm", children: result }))] }));
}
